import { Injectable, NgZone } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { BehaviorSubject, fromEvent, Subscription } from 'rxjs';
import { filter, throttleTime } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class AppUpdateService {
  private updateAvailableSubject = new BehaviorSubject<boolean>(false);
  public updateAvailable$ = this.updateAvailableSubject.asObservable();

  private isUpdatingSubject = new BehaviorSubject<boolean>(false);
  public isUpdating$ = this.isUpdatingSubject.asObservable();

  private currentHash: string | null = null;
  private isChecking = false;
  private lastCheckTime = 0;
  private isDismissed = false;
  private readonly checkThrottleMs = 120000; // 2 minutes throttle on route changes
  private subscriptions = new Subscription();

  constructor(private router: Router, private ngZone: NgZone) {
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      this.init();
    }
  }

  private init(): void {
    // 1. Detect the current main bundle hash from DOM
    this.currentHash = this.extractCurrentScriptHash();

    // 2. Initial check after 4 seconds (after initial page load stabilizes)
    setTimeout(() => {
      this.checkForUpdate();
    }, 4000);

    // 3. Purely event-driven: Check ONLY when user switches back to the app from background
    // (Zero background timers, zero battery consumption when app is inactive or screen off)
    const visibilitySub = fromEvent(document, 'visibilitychange').subscribe(() => {
      if (document.visibilityState === 'visible') {
        this.checkForUpdate();
      }
    });
    this.subscriptions.add(visibilitySub);

    // 4. Check on route navigation (throttled to at most once per 5 minutes)
    const navSub = this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        throttleTime(300000) // 5 minutes throttle
      )
      .subscribe(() => {
        this.checkForUpdate();
      });
    this.subscriptions.add(navSub);
  }

  /**
   * Extract hash of main.*.js from the current document's scripts
   */
  private extractCurrentScriptHash(): string | null {
    try {
      const scripts = Array.from(document.querySelectorAll('script[src]')) as HTMLScriptElement[];
      for (const script of scripts) {
        const src = script.getAttribute('src') || '';
        const match = src.match(/main\.([a-zA-Z0-9_-]+)\.js/);
        if (match && match[1]) {
          return match[1];
        }
      }
    } catch (e) {
      console.warn('[AppUpdate] Failed to extract current script hash:', e);
    }
    return null;
  }

  /**
   * Fetch index.html with cache-busting headers to see if a newer main.*.js hash exists on the server
   */
  public async checkForUpdate(): Promise<boolean> {
    if (this.isDismissed || this.isChecking) {
      return false;
    }

    const now = Date.now();
    // Guard against excessive calls within 15 seconds
    if (now - this.lastCheckTime < 15000) {
      return false;
    }
    this.lastCheckTime = now;
    this.isChecking = true;

    try {
      const basePath = document.querySelector('base')?.getAttribute('href') || '/';
      const cleanBasePath = basePath.endsWith('/') ? basePath : `${basePath}/`;
      const url = `${cleanBasePath}?_nocache=${now}`;

      const response = await fetch(url, {
        method: 'GET',
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });

      if (!response.ok) {
        this.isChecking = false;
        return false;
      }

      const html = await response.text();
      const match = html.match(/main\.([a-zA-Z0-9_-]+)\.js/);

      if (match && match[1]) {
        const serverHash = match[1];

        // If initial hash was not recorded yet, set it now
        if (!this.currentHash) {
          this.currentHash = serverHash;
          this.isChecking = false;
          return false;
        }

        if (serverHash !== this.currentHash) {
          console.info(`[AppUpdate] New version detected! Active: ${this.currentHash}, Server: ${serverHash}`);
          this.ngZone.run(() => {
            this.updateAvailableSubject.next(true);
          });
          this.isChecking = false;
          return true;
        }
      }
    } catch {
      // Quietly ignore network failures during background check
    } finally {
      this.isChecking = false;
    }

    return false;
  }

  /**
   * Reload the application to apply the latest updates
   */
  public applyUpdate(): void {
    if (typeof window !== 'undefined') {
      this.isUpdatingSubject.next(true);
      try {
        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.removeItem('wp_chunk_reload_ts');
        }
      } catch {}

      setTimeout(() => {
        window.location.reload();
      }, 300);
    }
  }

  /**
   * Dismiss notification for the current session
   */
  public dismiss(): void {
    this.isDismissed = true;
    this.updateAvailableSubject.next(false);
  }
}
